import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";

interface selectedInvoiceDataProps {
  selectedInvoice: any;
  orderinfo: any;
}

interface Invoice {
  invoiceNo: number; 
}

const FormattedAddress = ({ address, className }: { 
  address: {
    address1?: string;
    address2?: string;
    landmark?: string;
    areaName?: string;
    subDistrictName?: string;
    cityName?: string;
    stateName?: string;
    countryName?: string;
    zipcode?: string;
  };
  className?: string;
}) => {
  const formatAddressLine = (parts: (string | undefined)[], separator = ', ') => {
    const validParts = parts.filter(part => part && part.trim() !== '');
    return validParts.join(separator);
  };

  const addressLines = [
    address.address1,
    address.address2,
    address.landmark,
    formatAddressLine([
      address.areaName,
      address.subDistrictName,
    ]),
    formatAddressLine([
      address.cityName,
      address.stateName,
      address.countryName,
      
    ], ' ')
  ].filter(line => line && line.trim() !== '');

  return (
      <>
        {addressLines.map((line, index) => (
          <p key={`address-line-${index}`} className="mb-0">
            {line}
            {index !== addressLines.length - 1 ? ',' : '.'}
          </p>
        ))}
      </>
    );
};

const InviceItems: React.FC<selectedInvoiceDataProps> = ({ selectedInvoice, orderinfo }) => {
  const invoiceRef = useRef(null);
  const [firstInvoice, setFirstInvoice] = useState<any>();
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (orderinfo.invoices?.length > 0 && selectedInvoice) {
      setLoading(true);
      try {
        // Find the lowest invoice number
        const lowestInvoice = orderinfo.invoices.reduce((minInvoice: Invoice, currentInvoice: Invoice) => {
          return currentInvoice.invoiceNo < minInvoice.invoiceNo ? currentInvoice : minInvoice;
        });
        setFirstInvoice(lowestInvoice.invoiceNo);
        
        // Filter all items for the selected invoice
        const itemsForSelectedInvoice = orderinfo.invoices.filter(
          (inv: any) => inv.invoiceNo === selectedInvoice.invoiceNo
        );
        
        setInvoiceItems(itemsForSelectedInvoice);
      } finally {
        setLoading(false);
      }
    } else {
      setInvoiceItems([]);
    }
  }, [orderinfo, selectedInvoice]);

  const handleInvoicePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: 'Invoice Details',
    pageStyle: `@page { margin-top: 5mm; margin-left: 5mm; margin-right: 3mm; margin-bottom: 1mm; }`
  });

  const priceBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(invoice.invoiceUnitPrice);
  };
  
  const quantityBodyTemplate = (invoice: any) => {
    const matchingProduct = orderinfo.products?.find(
      (product: any) => product.productName === invoice.invoiceProductName
    );
    return invoice.invoicedQuantity + " (" + (matchingProduct?.measurementTypeCode || 'N/A') + ")";
  };

  const amountBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(invoice.invoicedQuantity * invoice.invoiceUnitPrice);
  };

  const taxBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(invoice.invoicedQuantity * invoice.invoiceUnitPrice * invoice.invoiceGST / 100);
  };

  const totalAmountBodyTemplate = (invoice: any) => {
    return Helpers.formatAmountInINR(Math.round(invoice.totalAmount));
  };

  const headerTemplate = (
    <>
      <div className="grid">
        <div className="col-6">
          <div className="grid mb-3">
            <img src={process.env.PUBLIC_URL + "/assests/images/DesiriLogo.png"} width={100} />
          </div>
          {orderinfo?.invoices[0]?.poS_Address1 && (
            <div>
              <div className="grid">
                <div className="col">From:<br /> <span className="text-primary">Desiri Naturals Pvt Ltd</span></div>
              </div>
              <div className="grid">

                  <div className="col">
                    <span className="text-primary">
                    {[
                      orderinfo.invoices[0].poS_Address1,
                      orderinfo.invoices[0].poS_Address2,
                      orderinfo.invoices[0].poS_CityName,
                      orderinfo.invoices[0].poS_LandMark,
                      orderinfo.invoices[0].poS_StateName,
                      orderinfo.invoices[0].poS_CountryName,
                    ].filter(Boolean).join(', ')}
                  </span><br />
                  {`Pin: ${orderinfo.invoices[0].poS_Zipcode}`}
                  </div>
              </div>
              <div className="grid">
                {orderinfo.invoices[0].poS_Address1 && (
                  <div className="col">
                    GST#: <span className="text-primary">29AAGCD5864J1Z4</span>
                  </div>
                )}
              </div>
            </div>
            )}        
          <div className="grid">
            <div className="col">To:<br/> <span className="text-primary">{orderinfo.userName}</span></div>
          </div>     
          <div className="grid">
            <div className="col">Delivery Address:<br/>  
              <span className="text-primary">
              <FormattedAddress 
                address={{
                  address1: orderinfo.address1,
                  address2: orderinfo.address2,
                  landmark: orderinfo.landmark,
                  areaName: orderinfo.areaName,
                  subDistrictName: orderinfo.subDistrictName,
                  cityName: orderinfo.cityName,
                  stateName: orderinfo.stateName,
                  countryName: orderinfo.countryName,                 
                }}               
              />
              </span>
              Pin: <span className="text-primary">{orderinfo.zipcode}</span>
            </div>
          </div>
          <div className="grid">
            <div className="col">Mobile #: <span className="text-primary">{orderinfo.primaryContactNbr}</span></div>
          </div>
          {orderinfo.gsT_Number !== null && (
            <div className="grid">
              <div className="col">GST #: <span className="text-primary">{orderinfo.gsT_Number}</span></div>
            </div>
          )}
        </div>
        <div className="col-1"></div>
        <div className="col-5">
          <div className="grid mt-7">
            <div className="col">Order #: <span className="text-primary">{orderinfo.orderID}</span></div>
          </div>
          <div className="grid">
            <div className="col">Order Date: <span className="text-primary">
              {new Date(orderinfo?.orderDate).toLocaleDateString("en-gb", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Invoice #: <span className="text-primary">{selectedInvoice?.invoiceNo}</span></div>
          </div>
          <div className="grid">
            <div className="col">Invoice Date: <span className="text-primary">
              {new Date(selectedInvoice?.invoicedDate).toLocaleDateString("en-gb", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Invoice Amount: <span className="text-primary">
              {Helpers.formatAmountInINR(selectedInvoice.invoiceGrossAmount)}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Payment Mode: <span className="text-primary">
              {selectedInvoice?.invoicePaymentModeName}
            </span></div>
          </div>
        </div>
      </div>
    </>
  );

  const footerTemplate = (
    <>
      <div className="grid">
        <div className="col-7"></div>
        <div className="col-5">
          <div className="grid">
            <div className="col">Taxable Amount:
              <div className="mr-2" style={{ float: "right" }}>
                {Helpers.formatAmountInINR(selectedInvoice.invoiceTaxableAmount)}
              </div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Tax Amount:
              <div className="mr-2" style={{ float: "right" }}>
                {Helpers.formatAmountInINR(selectedInvoice.invoiceTaxAmount)}
              </div>
            </div>
          </div>
          {
            orderinfo.handlingCharges > 0 ? 
                      <div className="grid">
            <div className="col">Handling Charges:
              <div className="mr-2" style={{ float: "right" }}>
                {selectedInvoice.invoiceNo === firstInvoice ?
                  Helpers.formatAmountInINR(orderinfo.handlingCharges) : '0'
                }
              </div>
            </div>
          </div> : ""
          }
          { orderinfo.courierCharges > 0 ?
            <div className="grid">
            <div className="col">Courier Charges:
              <div className="mr-2" style={{ float: "right" }}>
                {selectedInvoice.invoiceNo === firstInvoice ?
                  Helpers.formatAmountInINR(orderinfo.courierCharges) : '0'
                }
              </div>
            </div>
          </div> : ""
          }
          {orderinfo.packingCharges > 0 ?
            <div className="grid">
            <div className="col">Packing Charges:
              <div className="mr-2" style={{ float: "right" }}>
                {selectedInvoice.invoiceNo === firstInvoice ?
                  Helpers.formatAmountInINR(orderinfo.packingCharges) : '0'
                }
              </div>
            </div>
          </div> : ""
          }
          {selectedInvoice?.invoicePaymentGatewayCharges > 0 ?
                    <div className="grid">
            <div className="col">Payment Gateway Charges:
              <div className="mr-2" style={{ float: "right" }}>
                {selectedInvoice.invoiceNo === firstInvoice ?
                  Helpers.formatAmountInINR(selectedInvoice?.invoicePaymentGatewayCharges) : '0'
                }
              </div>
            </div>
          </div> : ""
          }
          {selectedInvoice.discountAmount > 0 && (
            <div className="grid">
              <div className="col">Discount Amount:
                <div className="mr-2" style={{ float: "right" }}>
                  {Helpers.formatAmountInINR(selectedInvoice.discountAmount)}
                </div>
              </div>
            </div>
          )}
          <div className="grid">
            <div className="col">Total Amount:
              <div className="mr-2 text-primary" style={{ float: "right" }}>
                {Helpers.formatAmountInINR(Math.round(selectedInvoice.invoiceTaxableAmount + selectedInvoice.invoiceTaxAmount + orderinfo.courierCharges - selectedInvoice.discountAmount))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="grid">
        <div className="col">Invoice Details
          <Button 
            icon="pi pi-print" 
            style={{ float: "right" }}
            onClick={handleInvoicePrint} 
            rounded 
            text 
            severity="info" 
            aria-label="User" 
          />
        </div>
      </div>
      <div ref={invoiceRef} style={{ overflowX: 'hidden' }}>
        <DataTable
          value={invoiceItems}
          className="no-flex"
          header={headerTemplate}
          footer={footerTemplate}
          size="small"
          loading={loading}
        >
          <Column field="invoiceProductName" header="Name" />
          <Column field="invoiceUnitPrice" header="Price" body={priceBodyTemplate} />
          <Column field="invoicedQuantity" header="Quantity" body={quantityBodyTemplate} />
          <Column header="Amount" body={amountBodyTemplate} />
          <Column header="Tax" body={taxBodyTemplate} />
          <Column field="totalAmount" header="Total" body={totalAmountBodyTemplate} />
        </DataTable>
      </div>
    </>
  );
};

export default InviceItems;